/**
 * Account timezone options (IANA).
 * Curated for Americas-first product usage; default matches DB.
 */

export const DEFAULT_ACCOUNT_TIMEZONE = 'America/Sao_Paulo'

export type AccountTimezoneOption = {
  value: string
  label: string
  region: 'south' | 'central' | 'north' | 'caribbean' | 'other'
}

/** Stable list used by Geral settings. Labels are city-oriented for operators. */
export const ACCOUNT_TIMEZONE_OPTIONS: readonly AccountTimezoneOption[] = [
  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', region: 'south' },
  { value: 'America/Manaus', label: 'Manaus (AMT)', region: 'south' },
  { value: 'America/Fortaleza', label: 'Fortaleza (BRT)', region: 'south' },
  { value: 'America/Belem', label: 'Belém (BRT)', region: 'south' },
  { value: 'America/Cuiaba', label: 'Cuiabá (AMT)', region: 'south' },
  { value: 'America/Porto_Velho', label: 'Porto Velho (AMT)', region: 'south' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (ACT)', region: 'south' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (FNT)', region: 'south' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (ART)', region: 'south' },
  { value: 'America/Santiago', label: 'Santiago (CLT)', region: 'south' },
  { value: 'America/Bogota', label: 'Bogotá (COT)', region: 'south' },
  { value: 'America/Lima', label: 'Lima (PET)', region: 'south' },
  { value: 'America/Caracas', label: 'Caracas (VET)', region: 'south' },
  { value: 'America/La_Paz', label: 'La Paz (BOT)', region: 'south' },
  { value: 'America/Asuncion', label: 'Assunção (PYT)', region: 'south' },
  { value: 'America/Montevideo', label: 'Montevidéu (UYT)', region: 'south' },
  { value: 'America/Guayaquil', label: 'Guayaquil (ECT)', region: 'south' },
  // Central / Caribbean
  { value: 'America/Mexico_City', label: 'Cidade do México (CST)', region: 'central' },
  { value: 'America/Panama', label: 'Panamá (EST)', region: 'central' },
  { value: 'America/Costa_Rica', label: 'Costa Rica (CST)', region: 'central' },
  { value: 'America/Guatemala', label: 'Guatemala (CST)', region: 'central' },
  { value: 'America/Havana', label: 'Havana (CST)', region: 'caribbean' },
  { value: 'America/Puerto_Rico', label: 'Porto Rico (AST)', region: 'caribbean' },
  // North America
  { value: 'America/New_York', label: 'New York (ET)', region: 'north' },
  { value: 'America/Chicago', label: 'Chicago (CT)', region: 'north' },
  { value: 'America/Denver', label: 'Denver (MT)', region: 'north' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)', region: 'north' },
  { value: 'America/Toronto', label: 'Toronto (ET)', region: 'north' },
  // Other common
  { value: 'Europe/Lisbon', label: 'Lisboa (WET)', region: 'other' },
  { value: 'Europe/Madrid', label: 'Madrid (CET)', region: 'other' },
  { value: 'UTC', label: 'UTC', region: 'other' },
] as const

const KNOWN = new Set(ACCOUNT_TIMEZONE_OPTIONS.map((o) => o.value))

export function isKnownAccountTimezone(value: unknown): value is string {
  return typeof value === 'string' && KNOWN.has(value)
}

/** Accept any non-empty IANA-looking string from DB (legacy / custom). */
export function normalizeAccountTimezone(value: unknown): string {
  if (typeof value === 'string' && value.trim()) return value.trim()
  return DEFAULT_ACCOUNT_TIMEZONE
}

export function formatTimezonePreview(
  timeZone: string,
  locale: string = 'pt-BR',
  at: Date = new Date()
): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(at)
  } catch {
    return new Intl.DateTimeFormat(locale, {
      timeZone: DEFAULT_ACCOUNT_TIMEZONE,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(at)
  }
}

/** Options for the select; includes current DB value if missing from curated list. */
export function timezoneSelectOptions(current: string): AccountTimezoneOption[] {
  if (KNOWN.has(current)) return [...ACCOUNT_TIMEZONE_OPTIONS]
  return [
    { value: current, label: current, region: 'other' },
    ...ACCOUNT_TIMEZONE_OPTIONS,
  ]
}
